import React from 'react';
import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useAuth } from '@front-activerse/common-components';
import { useTranslator } from '@front-activerse/common-components';
import { useApolloClient } from '@apollo/client';
import { UserDataQuery, formatIsoDate } from '@front-activerse/common-components';
import {
  Collapse,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  NavItem,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from 'reactstrap';

import { useAppContext } from '@front-activerse/common-components';
import LanguageChange from './LanguageChange';

const Header: React.FC = () => {
  const { isAuth } = useAuth();
  const { t, language } = useTranslator();

  const {
    images: { logo, signet, chevron, hamburger, close, login },
    navigation: { mainNavigation, userNavigation, logoutNavigation },
  } = useAppContext();

  const [mainMenuIsOpen, setMainMenuIsOpen] = useState(false);

  const client = useApolloClient();
  const userData = client.readQuery({
    query: UserDataQuery,
    variables: { date: formatIsoDate(new Date()) },
  });

  const userName = userData?.me?.name;

  const toggleMainMenu = () => {
    setMainMenuIsOpen(!mainMenuIsOpen);
  };

  return (
    <header className="mb-auto main-menu">
      <Navbar light expand="lg" container>
        <NavbarBrand tag={Link} to={import.meta.env.VITE_LANDING_PAGE_URL}>
          <img src={logo} alt={t('app.default.navbarTitle')} className="d-none d-lg-block" />
          <img src={signet} alt={t('app.default.navbarTitle')} className="d-lg-none" />
        </NavbarBrand>

        <div className="d-lg-none ms-auto">
          <LanguageChange displayLangName={false} />
        </div>

        <NavbarToggler onClick={toggleMainMenu}>
          <img src={hamburger} className={mainMenuIsOpen ? 'd-none' : ''} alt={t('menu.menu')} />
          <img src={close} className={mainMenuIsOpen ? '' : 'd-none'} alt="" />
        </NavbarToggler>

        <Collapse isOpen={mainMenuIsOpen} navbar>
          <div className="pt-3 d-lg-none" />
          {userName && (
            <>
              <div className="user-info">
                {t('menu.yourAccount')}
                <br />
                <strong>{userName}</strong>
              </div>
              <hr className="d-lg-none" />
            </>
          )}
          {/* main navigation */}
          {isAuth ? (
            <>
              <Nav className="me-auto ms-auto" navbar>
                {mainNavigation.map(
                  (item: {
                    path?: string;
                    translationKey: string;
                    externalPath?: string;
                    children?: { path: string; translationKey: string; externalPath?: string }[];
                    disabled?: boolean;
                  }) => {
                    if (item.children) {
                      return (
                        <UncontrolledDropdown nav inNavbar key={item.translationKey}>
                          <DropdownToggle nav caret>
                            {t(item.translationKey)}
                          </DropdownToggle>
                          <DropdownMenu>
                            {item.children.map((item) =>
                              item.externalPath ? (
                                <a
                                  href={item.externalPath}
                                  className="dropdown-item"
                                  key={item.translationKey}
                                >
                                  {t(item.translationKey)}
                                </a>
                              ) : (
                                <DropdownItem
                                  tag={NavLink}
                                  to={item.path}
                                  key={item.translationKey}
                                >
                                  {t(item.translationKey)}
                                </DropdownItem>
                              )
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      );
                    } else {
                      return (
                        <NavItem key={item.translationKey}>
                          {item.externalPath ? (
                            <a href={item.externalPath} className="nav-link">
                              {t(item.translationKey)}
                            </a>
                          ) : (
                            <NavLink
                              to={item.path || '#'}
                              className={item.disabled ? 'nav-link disabled' : 'nav-link'}
                              onClick={() => mainMenuIsOpen && toggleMainMenu()}
                            >
                              {t(item.translationKey)}
                            </NavLink>
                          )}
                        </NavItem>
                      );
                    }
                  }
                )}
              </Nav>

              <hr className="d-lg-none" />

              {/* user navigation */}
              <Nav className="d-lg-none" navbar>
                {userNavigation?.map(
                  (item: { path?: string; translationKey: string; disabled?: boolean }) => (
                    <NavItem key={item.translationKey}>
                      <NavLink
                        to={item.path || '#'}
                        className={item.disabled ? 'nav-link disabled' : 'nav-link'}
                        onClick={() => toggleMainMenu()}
                      >
                        {t(item.translationKey)}
                      </NavLink>
                    </NavItem>
                  )
                )}

                <hr className="d-lg-none" />

                <NavItem>
                  <NavLink to="/auth/logout" className="nav-link" onClick={() => toggleMainMenu()}>
                    <img src={login} className="me-1" alt="" />
                    {t('menu.logOut')}
                  </NavLink>
                </NavItem>
              </Nav>
            </>
          ) : (
            <>
              {/* logout navigation */}
              <Nav className="d-lg-none" navbar>
                {logoutNavigation.map(
                  (item: { path?: string; translationKey: string; externalPath?: any }) => {
                    if (item.externalPath) {
                      if (typeof item.externalPath === 'object') {
                        return (
                          <a
                            href={item.externalPath[language]}
                            className="nav-link"
                            key={item.translationKey}
                          >
                            {t(item.translationKey)}
                          </a>
                        );
                      }
                      return (
                        <a href={item.externalPath} className="nav-link" key={item.translationKey}>
                          {t(item.translationKey)}
                        </a>
                      );
                    }
                    return (
                      <NavItem key={item.translationKey}>
                        <NavLink to={item.path || '#'} className="nav-link">
                          {t(item.translationKey)}
                        </NavLink>
                      </NavItem>
                    );
                  }
                )}

                <hr className="d-lg-none" />

                <NavItem>
                  <NavLink to="/auth/login" className="nav-link" onClick={() => toggleMainMenu()}>
                    <img src={login} className="me-1" alt="" />
                    {t('menu.logIn')}
                  </NavLink>
                </NavItem>
              </Nav>
            </>
          )}
          <div className="pt-3 d-lg-none" />
        </Collapse>

        <div className="d-none d-lg-flex">
          <LanguageChange displayLangName={false} />

          {isAuth ? (
            <UncontrolledDropdown nav>
              <DropdownToggle nav className="dropdown-toggler cursor-pointer">
                {t('menu.account')}
                <img src={chevron} alt={t('menu.account')} className="ms-2" />
              </DropdownToggle>
              <DropdownMenu end className="mt-2">
                {userName && (
                  <>
                    <DropdownItem header className="user-select-auto">
                      {t('menu.yourAccount')}
                      <br />
                      <strong>{userName}</strong>
                    </DropdownItem>
                    <DropdownItem divider />
                  </>
                )}
                {userNavigation?.map(
                  (item: { path?: string; translationKey: string; disabled?: boolean }) => (
                    <DropdownItem
                      tag={NavLink}
                      to={item.path}
                      key={item.translationKey}
                      disabled={item.disabled}
                    >
                      {t(item.translationKey)}
                    </DropdownItem>
                  )
                )}

                <DropdownItem divider className="my-0" />

                <DropdownItem tag={NavLink} to="/auth/logout" key="logout">
                  <img src={login} className="me-1" alt="" />
                  {t('menu.logOut')}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          ) : (
            <UncontrolledDropdown nav>
              <DropdownToggle nav className="dropdown-toggler cursor-pointer">
                {t('menu.menu')}
                <img src={chevron} alt={t('menu.account')} className="ms-2" />
              </DropdownToggle>
              <DropdownMenu end className="mt-2">
                {logoutNavigation.map(
                  (item: { path?: string; translationKey: string; externalPath?: any }) => {
                    if (item.externalPath) {
                      if (typeof item.externalPath === 'object') {
                        return (
                          <a
                            href={item.externalPath[language]}
                            className="dropdown-item"
                            key={item.translationKey}
                          >
                            {t(item.translationKey)}
                          </a>
                        );
                      }
                      return (
                        <a
                          href={item.externalPath}
                          className="dropdown-item"
                          key={item.translationKey}
                        >
                          {t(item.translationKey)}
                        </a>
                      );
                    }
                    return (
                      <DropdownItem tag={NavLink} to={item.path} key={item.translationKey}>
                        {t(item.translationKey)}
                      </DropdownItem>
                    );
                  }
                )}

                {/* {logoutNavigation?.map(
                  (item: { path?: string; translationKey: string; disabled?: boolean }) => (
                    <DropdownItem
                      tag={NavLink}
                      to={item.path}
                      key={item.translationKey}
                      disabled={item.disabled}
                    >
                      {t(item.translationKey)}
                    </DropdownItem>
                  )
                )} */}

                <DropdownItem divider className="my-0" />

                <DropdownItem tag={NavLink} to="/auth/login" key="login">
                  <img src={login} className="me-1" alt="" />
                  {t('menu.logIn')}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          )}
        </div>
      </Navbar>
    </header>
  );
};

export default Header;
